import { useQuery } from '@tanstack/react-query';
import { BookOpen, GraduationCap, Lock } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { COMPETENCY_LABELS, type CompetencyLevel } from '@odr/shared';

import { PageHeader } from '../../components/AppShell';
import {
  Badge,
  Card,
  CardHeader,
  Callout,
  EmptyState,
  ListRow,
  Spinner,
  Stat,
  StatusPill,
} from '../../components/ui';
import { api } from '../../lib/api';
import { formatDate, humanise } from '../../lib/format';

interface TrainingRecord {
  user: { id: string; fullName: string; username: string };
  amSteps: Array<{
    stepNo: number;
    code: string;
    name: string;
    inPilot: boolean;
    level: CompetencyLevel;
    status: string;
    certifiedAt: string | null;
    assessedAt: string | null;
  }>;
  programs: Array<{
    id: string;
    programCode: string;
    programName: string;
    status: string;
    certifiedAt: string | null;
    notes: string | null;
  }>;
  opls: Array<{
    id: string;
    oplId: string;
    oplNo: string;
    title: string;
    status: string;
    assignedAt: string | null;
    completedAt: string | null;
    revision: number | null;
  }>;
  summary: { oplsAssigned: number; oplsCompetent: number; stepsCertified: number };
}

const LEVEL_TONE: Record<CompetencyLevel, 'neutral' | 'info' | 'warn' | 'ok'> = {
  NONE: 'neutral',
  AWARE: 'info',
  ASSISTED: 'warn',
  INDEPENDENT: 'ok',
  CAN_TEACH: 'ok',
};

export function TrainingPage() {
  const { userId } = useParams();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['training', userId ?? 'me'],
    queryFn: () =>
      api.get<TrainingRecord>(userId ? `/api/training/users/${userId}` : '/api/training/me'),
  });

  if (isLoading || !data) return <Spinner label="Loading training record" />;

  const pilotSteps = data.amSteps.filter((s) => s.inPilot);
  const futureSteps = data.amSteps.filter((s) => !s.inPilot);

  return (
    <div>
      <PageHeader
        title={userId ? data.user.fullName : 'My training'}
        subtitle="Autonomous Maintenance steps and One-Point Lesson competency"
      />

      <div className="space-y-4 p-4 lg:p-6">
        <div className="grid grid-cols-3 gap-3">
          <Stat
            label="AM steps certified"
            value={`${data.summary.stepsCertified} / ${pilotSteps.length}`}
            tone={data.summary.stepsCertified === pilotSteps.length ? 'ok' : 'info'}
          />
          <Stat label="OPLs assigned" value={data.summary.oplsAssigned} />
          <Stat
            label="OPLs competent"
            value={data.summary.oplsCompetent}
            tone={data.summary.oplsCompetent > 0 ? 'ok' : 'neutral'}
          />
        </div>

        <Card>
          <CardHeader
            title="Autonomous Maintenance steps"
            subtitle="The pilot covers Steps 0–3"
          />
          <ul className="divide-y divide-steel-100">
            {pilotSteps.map((step) => (
              <li key={step.stepNo} className="px-4 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-steel-900">
                      Step {step.stepNo} — {step.name}
                    </p>
                    {step.certifiedAt ? (
                      <p className="text-xs text-steel-500">
                        Certified {formatDate(step.certifiedAt)}
                      </p>
                    ) : step.assessedAt ? (
                      <p className="text-xs text-steel-500">
                        Assessed {formatDate(step.assessedAt)}
                      </p>
                    ) : (
                      <p className="text-xs text-steel-500">Not started</p>
                    )}
                  </div>
                  <StatusPill
                    tone={LEVEL_TONE[step.level]}
                    label={COMPETENCY_LABELS[step.level]}
                  />
                </div>
              </li>
            ))}
          </ul>
        </Card>

        <Card>
          <CardHeader
            title="Later steps"
            subtitle="Outside the current 3-month pilot"
          />
          <Callout tone="info" className="m-4">
            Steps 4–7 follow in later phases of the programme. They are shown here so the path is
            visible, but nobody can be certified on them yet.
          </Callout>
          <ul className="divide-y divide-steel-100">
            {futureSteps.map((step) => (
              <li key={step.stepNo} className="flex items-center gap-3 px-4 py-2.5 opacity-60">
                <Lock aria-hidden className="size-4 shrink-0 text-steel-400" />
                <span className="text-sm text-steel-600">
                  Step {step.stepNo} — {step.name}
                </span>
              </li>
            ))}
          </ul>
        </Card>

        <Card>
          <CardHeader title="One-Point Lessons" />
          {data.opls.length === 0 ? (
            <EmptyState
              icon={BookOpen}
              title="No lessons assigned"
              message="When your coach assigns a One-Point Lesson it will appear here."
            />
          ) : (
            <ul className="divide-y divide-steel-100">
              {data.opls.map((record) => (
                <li key={record.id}>
                  <ListRow onClick={() => navigate(`/opl/${record.oplId}`)}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-steel-900">
                          <span className="font-mono text-info-700">{record.oplNo}</span>{' '}
                          {record.title}
                        </p>
                        <p className="text-xs text-steel-500">
                          {record.revision ? `Revision ${record.revision}` : ''}
                          {record.completedAt ? ` · completed ${formatDate(record.completedAt)}` : ''}
                        </p>
                      </div>
                      <StatusPill
                        tone={
                          record.status === 'COMPETENT'
                            ? 'ok'
                            : record.status === 'NEEDS_RETRAINING'
                              ? 'alarm'
                              : record.status === 'TRAINING_COMPLETED'
                                ? 'info'
                                : 'warn'
                        }
                        label={humanise(record.status)}
                      />
                    </div>
                  </ListRow>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {data.programs.length > 0 ? (
          <Card>
            <CardHeader title="Training programmes" />
            <ul className="divide-y divide-steel-100">
              {data.programs.map((program) => (
                <li key={program.id} className="flex items-start justify-between gap-3 px-4 py-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-steel-900">{program.programName}</p>
                    {program.notes ? (
                      <p className="text-xs text-steel-500">{program.notes}</p>
                    ) : null}
                  </div>
                  <Badge
                    tone={
                      program.status === 'CERTIFIED'
                        ? 'ok'
                        : program.status === 'COMPLETED'
                          ? 'info'
                          : 'neutral'
                    }
                    icon={program.status === 'CERTIFIED' ? GraduationCap : undefined}
                  >
                    {humanise(program.status)}
                  </Badge>
                </li>
              ))}
            </ul>
          </Card>
        ) : null}
      </div>
    </div>
  );
}
